import React, { useState, useEffect } from 'react';
import EmployeeForm from '../../components/EmployeeForm';
import EmployeeTable from '../../components/EmployeeTable';
import { Employee, Positions, ShowModalProps } from '../../types/EmployeeTypes';
import BukManager from '../../components/BukManager';
import {
  getEmployees,
  addEmployee,
  deleteEmployee,
  updateEmployee,
  downloadFormat,
  importEmployees,
  exportEmployees,
  getEmployeesByReportDate,
  addEmployeeToReport,
  removeEmployeeFromReport
} from '../../api/employeeApi';
// --- Agregamos Tabs y Tab de Material-UI ---
import { Box, Typography, TextField, Button, Tabs, Tab } from '@mui/material';
import { getPositions } from '../../api/parametersApi';
import { Project } from '../../types/projectTypes';
import Loader from "../../components/Loader";
import ModalComponent from '../../components/ErrorsModal';

// Importar APIs de proyecto y usuario
import { getActiveProjects as getActiveProjectsFromProjectApi } from '../../api/projectApi';
import { getUserProject } from '../../api/userApi';
import { profiles, ADMIN_SISTEMA } from "../../contexts/constants/profiles";
import { DELETE_CONCURRENCY } from "../../contexts/constants/bulkDelete";

// --- Importamos el nuevo componente de Cargos ---
import PositionManager from '../../components/PositionManager';
import AttendanceManager from '../../components/AttendanceManager';
import { useCountry } from '../../contexts/CountryContext';

// Código de país para el que están habilitados los módulos BUK (integración solo Chile).
const BUK_COUNTRY_CODE = 'CL';

const Employees: React.FC = () => {
  // --- ESTADO PARA LAS PESTAÑAS (TABS) ---
  const [tabValue, setTabValue] = useState(0);

  // Los módulos BUK (Empleados BUK / Asistencia BUK) solo están disponibles cuando el país
  // seleccionado es Chile (y, por tanto, sus proyectos). Para el resto de países se ocultan.
  const { selectedCountry } = useCountry();
  const isBukEnabled = selectedCountry?.code === BUK_COUNTRY_CODE;

  // Estados de datos
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [positions, setPositions] = useState<Positions[]>([]);
  const [loading, setLoading] = useState<boolean>(false);

  // Estados de Filtros y Selección
  const [reportDate, setReportDate] = useState<string>("");
  const [selectedProject, setSelectedProject] = useState<boolean>(false);
  const [idProject, setIdProject] = useState<number | null>(null);

  // Estados de Modales
  const [showModal, setShowModal] = useState<ShowModalProps>({ show: false, message: "" });
  const [mostrarModal, setMostrarModal] = useState(false);
  const [modalTitle, setModalTitle] = useState<string | undefined>(undefined);
  const [modalSeverity, setModalSeverity] = useState<'error' | 'success' | 'warning'>('error');

  const mostrarError = (mensaje: string) => {
      setModalTitle(undefined);
      setModalSeverity('error');
      setShowModal({ show: true, message: mensaje });
      setMostrarModal(true);
  };

  // Muestra el resumen de una importación (empleados subidos y errores por fila)
  const mostrarResultadoImportacion = (result: { importados: number; fallidos: number; errores: string[]; mensaje: string }) => {
      const lineas: string[] = [result.mensaje];
      if (result.errores && result.errores.length > 0) {
          lineas.push('');
          lineas.push('Detalle de errores:');
          result.errores.forEach(e => lineas.push('• ' + e));
      }
      setModalTitle('Resultado de la importación');
      setModalSeverity(result.fallidos > 0 ? 'warning' : 'success');
      setShowModal({ show: true, message: lineas.join('\n') });
      setMostrarModal(true);
  };

  // Muestra el resumen de una eliminación múltiple (eliminados y motivo de cada fallo)
  const showDeleteResult = (deletedCount: number, errors: string[]) => {
      const lines: string[] = [
          deletedCount === 1 ? 'Se eliminó 1 registro.' : `Se eliminaron ${deletedCount} registros.`
      ];
      if (errors.length > 0) {
          lines.push('');
          lines.push(errors.length === 1 ? 'No se pudo eliminar 1 registro:' : `No se pudieron eliminar ${errors.length} registros:`);
          errors.forEach(e => lines.push('• ' + e));
      }
      setModalTitle('Resultado de la eliminación');
      setModalSeverity(errors.length > 0 ? 'warning' : 'success');
      setShowModal({ show: true, message: lines.join('\n') });
      setMostrarModal(true);
  };

  const cerrarModal = () => {
      setMostrarModal(false);
      setShowModal({ show: false, message: "" });
      setModalTitle(undefined);
      setModalSeverity('error');
  };

  // Función para manejar el cambio de pestaña
  // Si el usuario estaba en una pestaña BUK y cambia a un país sin BUK, vuelve a la primera.
  useEffect(() => {
    if (!isBukEnabled && (tabValue === 2 || tabValue === 3)) {
      setTabValue(0);
    }
  }, [isBukEnabled, tabValue]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  useEffect(() => {
    const loadCatalogs = async () => {
      setLoading(true);
      try {
        let rut = '';
        let role = '';
        const userStr = localStorage.getItem('user');
        if(userStr){
          const parsedUser = JSON.parse(userStr);
          rut = parsedUser.identificationNum;
          role = parsedUser.rols?.description || "";
        }

        const positionData = await getPositions();
        setPositions(positionData);

        let rawProjects: Project[] = [];

        if (role === profiles[0].name || role === profiles[2].name || role === ADMIN_SISTEMA) {
             rawProjects = await getActiveProjectsFromProjectApi();
        } else {
             const userProjects = await getUserProject(rut);
             rawProjects = Array.isArray(userProjects) ? userProjects : [];
        }

        const activeProjects = rawProjects.filter(p => p.status === undefined || p.status === true);
        setProjects(activeProjects);

      } catch (error) {
        console.error("Error cargando catálogos iniciales", error);
        mostrarError("Error al cargar listado de proyectos.");
      } finally {
        setLoading(false);
      }
    };

    loadCatalogs();
    fetchEmployeesData();
  }, []);

  // ===========================================================================
  // 2. EFECTO DE RECARGA DE TABLA (EMPLEADOS)
  // ===========================================================================
  useEffect(() => {
    if (selectedProject || idProject) {
        fetchEmployeesData();
    }
  }, [idProject, reportDate]);

  const fetchEmployeesData = async () => {
      setLoading(true);
      try {
        let fetchedEmployees: Employee[] = [];

        if (idProject && reportDate) {
            fetchedEmployees = await getEmployeesByReportDate(idProject, reportDate);
        } else {
            fetchedEmployees = await getEmployees();
        }
        setEmployees(fetchedEmployees);
      } catch (error) {
        console.error("Error cargando empleados", error);
        if (idProject && reportDate) setEmployees([]);
      } finally {
        setLoading(false);
      }
  };

  const simplifiedProjects = projects
    .filter(project => project.id !== undefined && project.name !== undefined)
    .map((project) => ({
        ...project,
        id: project.id as number,
        name: project.name as string,
        costCenter: project.costCenter || "",
        status: project.status ?? true
    }));

  const handleAddEmployee = async (newEmployee: Omit<Employee, 'id'>, workFrontId?: number) => {
    try {
      if (idProject && reportDate) {
          if (!workFrontId) throw new Error("Falta seleccionar el Frente");
          await addEmployeeToReport(idProject, reportDate, newEmployee, workFrontId);
      } else {
          await addEmployee(newEmployee);
      }
      fetchEmployeesData();
    } catch (error: any) {
      console.error("Error al agregar empleado", error);
      const backendMessage = error.response?.data?.mensaje || error.message || "Error al agregar";
      mostrarError(backendMessage);
      throw error;
    }
  };

  const handleEditEmployee = async (oldRut: string, updatedEmployee: Employee) => {
    if (reportDate) {
        mostrarError("No se pueden editar datos personales en un reporte histórico.");
        return;
    }

    try {
      await updateEmployee(updatedEmployee, oldRut);
      fetchEmployeesData();
    } catch (error: any) {
      console.error("Error al editar empleado", error);
      const backendMessage = error.response?.data?.mensaje || error.response?.data?.error || "Error al editar empleado";
      mostrarError(backendMessage);
      throw error;
    }
  };

  const handleDeleteEmployee = async (employeeId: string) => {
      try {
        if (idProject && reportDate) {
            await removeEmployeeFromReport(idProject, reportDate, employeeId);
        } else {
            await deleteEmployee(employeeId);
        }
        setEmployees(employees.filter((employee) => employee.rut !== employeeId));
      } catch (error: any) {
         console.error("Error al eliminar", error);
         const backendMessage = error.response?.data?.mensaje || "Error al eliminar";
         mostrarError(backendMessage);
      }
  };

  // Eliminación múltiple: no hay endpoint masivo, así que se recorre la selección llamando
  // al endpoint de borrado individual. Se procesa en tandas para no encolar una request por
  // registro de forma secuencial (con 286 empleados eso serían ~57 s de espera).
  const handleDeleteMultipleEmployees = async (employeeIds: string[]) => {
      const results: { rut: string; error?: string }[] = [];

      for (let i = 0; i < employeeIds.length; i += DELETE_CONCURRENCY) {
          const batch = employeeIds.slice(i, i + DELETE_CONCURRENCY);

          const batchResults = await Promise.all(batch.map(async (rut) => {
              const employee = employees.find(e => e.rut === rut);
              const label = employee
                  ? `${rut} - ${[employee.name, employee.firstName, employee.lastName].filter(Boolean).join(' ')}`
                  : rut;
              try {
                  if (idProject && reportDate) {
                      await removeEmployeeFromReport(idProject, reportDate, rut);
                  } else {
                      await deleteEmployee(rut);
                  }
                  return { rut };
              } catch (error: any) {
                  console.error("Error al eliminar", error);
                  const backendMessage = error.response?.data?.mensaje || "Error al eliminar";
                  return { rut, error: `${label}: ${backendMessage}` };
              }
          }));

          // Se acumula en el orden de la selección, no en el orden de respuesta del servidor.
          results.push(...batchResults);
      }

      const deletedRuts = results.filter(r => !r.error).map(r => r.rut);
      const errors = results.filter(r => r.error).map(r => r.error as string);

      if (deletedRuts.length > 0) {
          setEmployees(prev => prev.filter(employee => !deletedRuts.includes(employee.rut || '')));
      }
      showDeleteResult(deletedRuts.length, errors);
  };

  const handleDownloadFormat = async () => {
    try {
      setLoading(true);
      await downloadFormat();
      setLoading(false);
    } catch (error) {
      console.error('Error al descargar:', error);
      setLoading(false);
    }
  };

  const handleImportEmployees = async (file: File) => {
    try {
      setLoading(true);
      if(file) {
        const result = await importEmployees(file);
        await fetchEmployeesData();
        mostrarResultadoImportacion(result);
      }
      setLoading(false);
    } catch (error: any) {
      console.error('Error al importar:', error);
      const backendMessage = error.response?.data?.mensaje || "Error al importar.";
      mostrarError(backendMessage);
      setLoading(false);
    }
  };

  const handleExportEmployees = async () => {
    try {
      setLoading(true);
      await exportEmployees();
      setLoading(false);
    } catch (error) {
      console.error('Error al exportar:', error);
      setLoading(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      {loading && (<Loader />) }

      <Typography variant="h4" gutterBottom>
        Gestión de Personal
      </Typography>

      {/* --- PESTAÑAS (TABS) --- */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', bgcolor: '#ffffff', mb: 3 }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          centered
          sx={{
            '& .MuiTab-root': { fontWeight: 'bold', textTransform: 'none', fontSize: '1rem' },
            '& .Mui-selected': { color: '#0056b3' },
            '& .MuiTabs-indicator': { backgroundColor: '#0056b3' }
          }}
        >
          <Tab label="Lista de Personal" value={0} />
          <Tab label="Mantenedor de Cargos" value={1} />
          {isBukEnabled && <Tab label="Empleados BUK" value={2} />}
          {isBukEnabled && <Tab label="Asistencia BUK" value={3} />}
        </Tabs>
      </Box>

      {/* --- CONTENIDO PESTAÑA 0: LISTA DE PERSONAL --- */}
      {tabValue === 0 && (
        <Box>
          {selectedProject && (
            <Box sx={{ mb: 3, p: 2, bgcolor: '#e3f2fd', borderRadius: 2, display: 'flex', alignItems: 'center', gap: 2, boxShadow: 1 }}>
                <Typography variant="body1" sx={{ fontWeight: 'bold', color: '#0d47a1' }}>
                    Modo Reporte Diario:
                </Typography>
                <TextField
                    label="Seleccionar Fecha"
                    type="date"
                    value={reportDate}
                    onChange={(e) => setReportDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    size="small"
                    sx={{ width: 220, bgcolor: 'white' }}
                    helperText={reportDate ? "Editando personal histórico" : "Viendo personal actual"}
                />
                {reportDate && (
                    <Button variant="outlined" color="secondary" size="small" onClick={() => setReportDate("")}>
                        Volver a Hoy
                    </Button>
                )}
            </Box>
          )}

          <EmployeeForm
            projects={simplifiedProjects}
            positions={positions}
            onAddEmployee={handleAddEmployee}
            setSelectedProject={setSelectedProject}
            setIdProject={setIdProject}
            reportDate={reportDate}
            idProject={idProject}
          />

          <Box sx={{ mt: 4 }}>
            <EmployeeTable
              employees={employees}
              projects={simplifiedProjects}
              selectedProject={selectedProject}
              positions={positions}
              onEditEmployee={handleEditEmployee}
              onDeleteEmployee={handleDeleteEmployee}
              onDeleteMultipleEmployees={handleDeleteMultipleEmployees}
              onDownloadFormat={handleDownloadFormat}
              onImportEmployees={handleImportEmployees}
              onExportEmployees={handleExportEmployees}
              reportDate={reportDate}
            />
          </Box>
        </Box>
      )}

      {tabValue === 1 && (
        <Box>
          <PositionManager />
        </Box>
      )}
      {isBukEnabled && tabValue === 2 && (
          <Box>
            <BukManager />
          </Box>
      )}
      {isBukEnabled && tabValue === 3 && (
          <Box>
            <AttendanceManager />
          </Box>
      )}

      <ModalComponent
        open={mostrarModal}
        onClose={cerrarModal}
        message={showModal.message}
        title={modalTitle}
        severity={modalSeverity}
      />
    </Box>
  );
};

export default Employees;